import React, { Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { PAGES, type PageDef } from './pages';
import { PendingPage } from './PendingPage';
import { Shell } from './Shell';
import { AppFrame } from './AppFrame';

// Development tools: the condition keeps them out of a production build (the live test page signs in to the mock).
const dev = import.meta.env.DEV
  ? {
      Theme: React.lazy(() => import('../dev/ThemeTestPage').then((m) => ({ default: m.ThemeTestPage }))),
      Live: React.lazy(() => import('../dev/LiveTestPage').then((m) => ({ default: m.LiveTestPage }))),
      Gallery: React.lazy(() => import('../ds/DsGallery').then((m) => ({ default: m.DsGallery }))),
      Preview: React.lazy(() => import('../ds/ComponentPreview').then((m) => ({ default: m.ComponentPreview }))),
    }
  : null;

const devPage = (C: React.ComponentType) => (
  <Suspense fallback={null}>
    <C />
  </Suspense>
);

// Every page lives in src/pages/<ID>/index.tsx (default export). Pages without a module yet
// render PendingPage, so every PAGES.md route exists from the start. SHL-01..04 are the design's
// reference pages, drawn with its sample data: the real shell, overlays and states are on every
// page, so these stay out of a production build, like the tools above.
type PageModule = { default: React.ComponentType<{ page: PageDef }> };
const modules = import.meta.env.DEV
  ? import.meta.glob<PageModule>('../pages/*/index.tsx')
  : import.meta.glob<PageModule>(['../pages/*/index.tsx', '!../pages/SHL-*/index.tsx']);
const routed = import.meta.env.DEV ? PAGES : PAGES.filter((p) => !p.id.startsWith('SHL-'));
const lazyCache = new Map<string, React.LazyExoticComponent<React.ComponentType<{ page: PageDef }>>>();

export function pageComponent(id: string): React.ComponentType<{ page: PageDef }> | null {
  const load = modules['../pages/' + id + '/index.tsx'];
  if (!load) return null;
  let c = lazyCache.get(id);
  if (!c) {
    c = React.lazy(load);
    lazyCache.set(id, c);
  }
  return c;
}

function PageRoute({ page }: { page: PageDef }) {
  const C = pageComponent(page.id);
  const loc = useLocation();
  // pages without the shell (auth) render their own `.bx-app` root through <AppFrame>
  const pending = page.shell ? (
    <PendingPage page={page} />
  ) : (
    <AppFrame>
      <PendingPage page={page} />
    </AppFrame>
  );
  return (
    <Suspense fallback={page.shell ? <main className="bx-main" aria-busy="true" /> : <AppFrame>{null}</AppFrame>}>
      {C ? <C key={loc.pathname} page={page} /> : pending}
    </Suspense>
  );
}

export function AppRoutes() {
  const shell = routed.filter((p) => p.shell);
  const bare = routed.filter((p) => !p.shell);
  return (
    <Routes>
      {bare.map((p) => (
        <Route key={p.id} path={'/' + p.slug} element={<PageRoute page={p} />} />
      ))}
      <Route element={<Shell />}>
        {shell.map((p) => (
          <Route key={p.id} path={'/' + p.slug} element={<PageRoute page={p} />} />
        ))}
      </Route>
      {dev ? (
        <>
          <Route path="/dev/theme" element={devPage(dev.Theme)} />
          <Route path="/dev/live" element={devPage(dev.Live)} />
          <Route path="/ds" element={devPage(dev.Gallery)} />
          <Route path="/ds/components/:name" element={devPage(dev.Preview)} />
        </>
      ) : null}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Shell notFound />} />
    </Routes>
  );
}
